import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { TableCell, TableRow } from '@/components/ui/table';
import { Check, X, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: component in src/pages/insurance/claim-detail/ requires four levels up
import type { TClaimLineItemStatus } from '@/@types/claims';
import {
  LINE_ITEM_STATUS_BADGE,
  LINE_ITEM_STATUS_BADGE_VARIANT,
  formatMoney,
  formatDate,
} from './claimDetailUtils';
import type { TLineItem, TBenefitItem, TUtilization } from './claimDetailTypes';

interface IClaimLineItemRowProps {
  item: TLineItem;
  currency: string;
  benefitItems: TBenefitItem[];
  utilization: TUtilization[];
  onUpdateLineItem: (args: {
    lineItemId: Id<'claimLineItems'>;
    status?: string;
    amountApproved?: number;
    amountPaid?: number;
    rejectionReason?: string;
    benefitItemId?: Id<'benefitItems'>;
  }) => Promise<void>;
  onRecordPayment: (lineItemId: Id<'claimLineItems'>, amount: number) => Promise<void>;
}

export function ClaimLineItemRow({
  item,
  currency,
  benefitItems,
  utilization,
  onUpdateLineItem,
  onRecordPayment,
}: IClaimLineItemRowProps) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approveAmount, setApproveAmount] = useState(
    String(item.amountApproved ?? item.amountClaimed)
  );
  const [payAmount, setPayAmount] = useState(
    String(item.amountPaid ?? item.amountApproved ?? item.amountClaimed)
  );
  const [isBusy, setIsBusy] = useState(false);

  const mappedUtil = useMemo(
    () =>
      item.benefitItemId
        ? utilization.find(u => u.benefitItemId === item.benefitItemId)
        : undefined,
    [item.benefitItemId, utilization]
  );
  const mappedItem = useMemo(
    () => (item.benefitItemId ? benefitItems.find(b => b._id === item.benefitItemId) : undefined),
    [item.benefitItemId, benefitItems]
  );

  const lineStatus = item.status as TClaimLineItemStatus; // AS_JUSTIFIED: Convex return status string narrowed to TClaimLineItemStatus for badge/action dispatch

  async function handleApprove() {
    const parsed = parseFloat(approveAmount);
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setIsBusy(true);
    try {
      await onUpdateLineItem({ lineItemId: item._id, status: 'approved', amountApproved: parsed });
      toast.success('Line item approved');
    } catch {
      toast.error('Failed to approve line item');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleReject() {
    if (!rejectReason.trim()) {
      toast.error('Enter a rejection reason');
      return;
    }
    setIsBusy(true);
    try {
      await onUpdateLineItem({
        lineItemId: item._id,
        status: 'rejected',
        rejectionReason: rejectReason.trim(),
      });
      toast.success('Line item rejected');
      setRejectOpen(false);
    } catch {
      toast.error('Failed to reject line item');
    } finally {
      setIsBusy(false);
    }
  }

  async function handlePay() {
    const parsed = parseFloat(payAmount);
    if (isNaN(parsed) || parsed < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setIsBusy(true);
    try {
      await onRecordPayment(item._id, parsed);
      toast.success('Payment recorded');
    } catch {
      toast.error('Failed to record payment');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleBenefitMap(value: string) {
    setIsBusy(true);
    try {
      await onUpdateLineItem({ lineItemId: item._id, benefitItemId: value as Id<'benefitItems'> }); // AS_JUSTIFIED: Id cast from Select string value
    } catch {
      toast.error('Failed to map benefit item');
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <>
      <TableRow>
        <TableCell className="text-sm text-foreground">{formatDate(item.dateOfService)}</TableCell>
        <TableCell className="text-sm text-foreground max-w-[180px]">
          <p className="truncate" title={item.description}>
            {item.description}
          </p>
        </TableCell>
        <TableCell className="text-sm text-foreground">
          {formatMoney(item.amountClaimed, currency)}
        </TableCell>
        <TableCell>
          {lineStatus === 'pending' ? (
            <Input
              type="number"
              value={approveAmount}
              onChange={e => setApproveAmount(e.target.value)}
              className="h-7 w-28 text-xs border-border"
              min={0}
            />
          ) : (
            <span className="text-sm text-foreground">
              {item.amountApproved != null ? formatMoney(item.amountApproved, currency) : '—'}
            </span>
          )}
        </TableCell>
        <TableCell className="text-sm">
          {item.amountPaid != null ? formatMoney(item.amountPaid, currency) : '—'}
        </TableCell>
        <TableCell>
          <Badge
            variant={LINE_ITEM_STATUS_BADGE_VARIANT[lineStatus]}
            className={cn('text-xs capitalize', LINE_ITEM_STATUS_BADGE[lineStatus])}
          >
            {lineStatus}
          </Badge>
        </TableCell>
        <TableCell>
          <Select
            value={item.benefitItemId ?? ''}
            onValueChange={handleBenefitMap}
            disabled={isBusy || benefitItems.length === 0}
          >
            <SelectTrigger className="h-7 w-36 text-xs cursor-pointer">
              <SelectValue placeholder="Map benefit…" />
            </SelectTrigger>
            <SelectContent>
              {benefitItems.map(b => (
                <SelectItem key={b._id} value={b._id} className="text-xs">
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mappedItem && mappedUtil && (
            <p className="text-xs text-muted-foreground mt-1">
              {formatMoney(mappedUtil.amountUsed, currency)} /{' '}
              {mappedItem.annualLimit != null ? formatMoney(mappedItem.annualLimit, currency) : '∞'}
            </p>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {lineStatus === 'pending' && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={handleApprove}
                disabled={isBusy}
              >
                <Check className="h-3 w-3 mr-1 text-claim-approved" /> Approve
              </Button>
            )}
            {lineStatus === 'pending' && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => setRejectOpen(true)}
                disabled={isBusy}
              >
                <X className="h-3 w-3 mr-1" /> Reject
              </Button>
            )}
            {lineStatus === 'approved' && (
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  value={payAmount}
                  onChange={e => setPayAmount(e.target.value)}
                  className="h-7 w-20 text-xs"
                  min={0}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={handlePay}
                  disabled={isBusy}
                >
                  <DollarSign className="h-3 w-3 mr-1" /> Pay
                </Button>
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      {item.rejectionReason && (
        <TableRow className="hover:bg-transparent border-0">
          <TableCell colSpan={8} className="pt-0 pb-3 pl-8">
            <div className="rounded-md border border-claim-rejected-border bg-claim-rejected-bg px-4 py-3">
              <p className="text-xs font-medium text-claim-rejected mb-1">Rejection Reason</p>
              <p className="text-sm text-claim-rejected">{item.rejectionReason}</p>
            </div>
          </TableCell>
        </TableRow>
      )}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Rejection Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Explain why this line item is being rejected…"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRejectOpen(false)}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button variant="destructive" size="sm" onClick={handleReject} disabled={isBusy}>
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
